<template>
  <div
    class="queue-card rounded-tr-md"
    :class="borderColor"
    @click="$emit('click')"
  >
    <span
      v-if="appointment.manual"
      class="manualSpan"
    >manual</span>
    <div
      v-if="newPatient"
      class="corner-flag absolute top-0 right-0 w-8 h-5"
      style="padding-top: 2px;"
      :style="{ paddingTop: newPatient ? '2px' : '0px' }"
    >
      <div class="flex justify-center items-center">
        <v-icon color="#F57A1F" :size="18">{{ iconPath }}</v-icon>
      </div>
    </div>
    <div
      v-else
      class="corner-flag absolute top-0 right-0 pl-2 h-5 flex flex-wrap flex-row-reverse w-3/4"
    >

      <div
        v-for="(x, i) in doctors.slice(0, 3)" :key="!!x ? x.uuid : i"
        class="text-xs text-white font-bold text-center w-7 h-5" :style="getInitialsStyle(x)"
        v-text="getInitials(x)"
      ></div>
      <div
        v-if="doctors.length > 6"
        key="moreTagInQueue"
        class="text-xs text-white font-bold text-center w-7 h-5" :style="{backgroundColor: '#efefef', color: '#333333'}"
        v-text="`+ ${doctors.length - 5}`"
      ></div>
      <div
          v-for="(x, i) in doctors.slice(3, 5)" :key="!!x ? x.uuid : i"
          class="text-xs text-white font-bold text-center w-7 h-5" :style="getInitialsStyle(x)"
          v-text="getInitials(x)"
      ></div>
    </div>
    <div class="patient">
      <Avatar class="mb-1" :src="imageUrl" :alt="name" />
      <div class="text-sm text-gray-darker truncate">
        {{ patientName(name) }}
      </div>

      <div class="flagsWrapper">
        <v-icon
            v-if="appointment.has_traction_flags"
            color="green-darken-2"
            :size="17">mdi-flag</v-icon>
        <v-icon
            v-if="appointment.has_flags"
            color="red-darken-2"
            :size="17">mdi-flag</v-icon>
        <v-icon
            v-if="telehealthRoom"
            color="red-darken-2"
            :size="17"
            title="Online video visit">mdi-video</v-icon>
      </div>
    </div>
    <div class="timestamps">
      <div v-if="arrivalTime" class="text-sm">
        <span class="absolute font-bold text-gray" :style="{ color: waitTimeTextColor }">
          Wait |
        </span>
        <span class="ml-10" :style="{ color: waitTimeTextColor }">
          {{ waitTime }}{{ overHour ? 'm' : 's'}}
        </span>
      </div>
      <div class="text-xs">
        <span class="absolute text-gray">
          Appt |
        </span>
        <span class="ml-10" :class="isMoreThanTenMinLate ? 'red--text' : ''">
          {{ $dayjs(appointmentTime).format('h:mm A') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mdiStar } from '@mdi/js';
import randomColor from 'randomcolor';
import { defineComponent } from 'vue';
import { mapActions, mapState } from 'vuex';
import { uniqBy } from 'lodash-es';

import Avatar from '@/components/basic/Avatar.vue';

export default defineComponent({
  name: 'QueueCard',
  components: { Avatar },
  props: {
    uuid: { type: [String, Number], required: true },
    appointment: { type: Object, required: true },
    name: { type: String, required: true },
    imageUrl: { type: String, required: false },
    doctorInitials: { type: String, required: true },
    newPatient: { type: Boolean, default: false },
    appointmentTime: { type: [Date, String], required: true },
    arrivalTime: { type: [Date, String], required: false },
    treatments: { type: Array, required: true },
    allowMakeManual: { type: Boolean, default: false },
  },
  data() {
    return {
      iconPath: mdiStar,
      overHour: false,
      interval: undefined,
      count: 0,
      interactWithBoard: false,
    };
  },
  async mounted() {
    this.interactWithBoard = await this.$store.dispatch('ableUserTo', 'interact with board');
  },
  computed: {
    ...mapState(['appointments', 'time']),
    borderColor() {
      if (this.waitTimeMinutes < 5) { return 'border--default'; }
      else if (this.waitTimeMinutes < 10) { return 'border--orange'; }
      else if (this.waitTimeMinutes > 9) { return 'border--red'; }
    },
    doctors() {
      const doctors = this.appointment.doctors.map((t) => t.doctor);
      return uniqBy(doctors, 'uuid');
    },
    telehealthRoom() {
      return this.appointment.doctors?.find((t) => t.telehealth?.url)?.telehealth ?? null;
    },
    isLate() {
      const a = this.$dayjs(this.appointmentTime);
      const b = this.$dayjs(this.arrivalTime);
      return b.isAfter(a);
    },
    isMoreThanTenMinLate() {
      if (!this.arrivalTime) { return false; }
      const a = this.$dayjs(this.appointmentTime);
      const b = this.$dayjs(this.arrivalTime);
      const diff = b.diff(a, 'minute');
      return diff >= 10;
    },
    waitTime() {
      const arrival = this.$dayjs(this.arrivalTime);
      const ms = this.$dayjs(this.time).diff(arrival, 's') * 1000;
      const dur = this.$dayjs.duration(ms).asMilliseconds();
      const format = ms >= 3600000 ? 'hh:mm' : 'mm:ss';
      this.overHour = format === 'hh:mm';
      return this.$dayjs.utc(dur).format(format);
    },
    waitTimeMinutes() { return parseInt(this.waitTime.split(':')[0]); },
    waitTimeTextColor() {
      if (this.waitTimeMinutes < 5) { return 'black'; }
      else if (this.waitTimeMinutes < 10) { return 'orange'; }
      else if (this.waitTimeMinutes > 9) { return '#E83123'; }
    },
  },
  methods: {
    ...mapActions([
      'selectAppointment',
    ]),
    getInitials(x: any) {
      return this.$filters.initials(x?.name ?? 'X X');
    },
    getInitialsStyle(x: any) {
      const backgroundColor =
        x?.color ?? (this.getInitials(x) === 'XX'
          ? '#E83123'
          : randomColor()
        );
      return {
        backgroundColor,
        color: this.$filters.foregroundColor(backgroundColor),
      };
    },
    patientName(name) {
      return name.slice(0, 3) === 'Dr.' ? name.slice(4) : name;
    },
    startAppointment() {
      this.selectAppointment({
        appointment: this.appointments.find(({ uuid }) => uuid === this.uuid),
      });
    },
  },
});
</script>

<style scoped>
.queue-card {
  overflow: hidden;
  max-width: 128px;
  @apply w-full rounded-md bg-white p-1 flex flex-col relative cursor-pointer;
}

.border--default {
  border: none;
}

.border--orange {
  border: 2px solid orange;
}

.border--red {
  border: 2px solid #e83123;
}

.manualSpan {
  position: absolute;
  z-index: 1;
  background-color: #086C97;
  color: #ffffff;
  font-size: 10px;
  right: 3px;
  top: 23px;
  border-radius: 20px;
  padding: 2px 5px;
}

.flagsWrapper {
  position: absolute;
  bottom: 3px;
  right: 0;

  display: flex;
  justify-content: flex-end;
}
</style>
